import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectRevisions, useRevisionPendingImages, type RevisionEntry } from '../../hooks/useProjects.js';
import { DrawerSurface } from './PendingImagesDrawer.js';
import { Button } from '../ui/Button.js';
import { Pressable } from '../ui/Pressable.js';
import { parseServerDate } from '../../lib/date.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface RevisionsLedgerProps {
  projectId: number;
  /** When provided, renders a "New revision" button in the ledger header. */
  onNewRevision?: () => void;
  isNewRevisionPending?: boolean;
  newRevisionError?: string | null;
}

interface RevisionItemProps {
  entry: RevisionEntry;
  projectId: number;
  isOpen: boolean;
  onToggle: () => void;
  onOpenImages: () => void;
}

// ── RevisionItem ─────────────────────────────────────────────────────────────
// Single accordion row. Uses grid-template-rows transition (Emil principle:
// animate grid rows, not height directly — interruptible, no layout thrash).
// Iframe is lazy-mounted: only rendered while the item is open.

function RevisionItem({ entry, projectId, isOpen, onToggle, onOpenImages }: RevisionItemProps) {
  const { t, i18n } = useTranslation();

  const approvedDate = parseServerDate(entry.approvedAt).toLocaleDateString(i18n.language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const previewUrl = `/api/projects/${projectId}/revisions/${entry.revisionNumber}/preview/index.html`;
  const pendingCount = entry.pendingImagesSummary.total - entry.pendingImagesSummary.uploaded;

  return (
    <div
      style={{
        borderBottom: '1px solid var(--evo-stroke)',
      }}
    >
      {/* Header — keyboard and mouse toggle */}
      <Pressable
        fullWidth
        ariaExpanded={isOpen}
        onPress={onToggle}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--evo-space-3)',
          padding: 'var(--evo-space-3) var(--evo-space-4)',
          textAlign: 'left',
          userSelect: 'none',
        }}>
          {/* Chevron indicator — rotates 90° when open */}
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '14px',
              height: '14px',
              flexShrink: 0,
              color: 'var(--evo-fg-3)',
              fontSize: '10px',
              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 220ms ease-out',
            }}
          >
            ▶
          </span>

          {/* Label */}
          <span
            style={{
              fontSize: 'var(--evo-fs-label)',
              fontWeight: 'var(--evo-fw-medium)',
              color: 'var(--evo-fg-1)',
              fontFamily: 'var(--evo-font)',
              flex: 1,
              minWidth: 0,
            }}
          >
            {t('projects.revisions.headerLabel', {
              n: entry.revisionNumber,
              date: approvedDate,
            })}
          </span>

          {/* Right side — action buttons when open, summary chips when closed */}
          {isOpen ? (
            /* Action buttons — shown only when expanded */
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)', flexShrink: 0 }}
              onClick={e => e.stopPropagation()}
            >
              {entry.pendingImagesSummary.total > 0 && (
                <Button variant="outline" tone="muted" size="sm" onClick={onOpenImages}>
                  {t('projects.revisions.imagesButton', { count: pendingCount, defaultValue: 'Images ({{count}})' })}
                </Button>
              )}
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontSize: 'var(--evo-fs-label)',
                  color: 'var(--evo-cyan)',
                  fontFamily: 'var(--evo-font)',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {t('projects.revisions.openNewTab')}
              </a>
            </div>
          ) : (
            /* Summary chips — visible when collapsed */
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--evo-space-3)',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: 'var(--evo-fs-caption)',
                  color: 'var(--evo-fg-3)',
                  fontFamily: 'var(--evo-font)',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('projects.revisions.iterations', { count: entry.workingVersionsCount })}
              </span>
              {entry.pendingImagesSummary.total > 0 && (
                <span
                  style={{
                    fontSize: 'var(--evo-fs-caption)',
                    color: 'var(--evo-fg-3)',
                    fontFamily: 'var(--evo-font)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('projects.revisions.imagesUploaded', {
                    uploaded: entry.pendingImagesSummary.uploaded,
                    total: entry.pendingImagesSummary.total,
                  })}
                </span>
              )}
            </span>
          )}
        </div>
      </Pressable>

      {/* Expandable body — grid-template-rows technique for smooth height animation */}
      <div
        aria-hidden={!isOpen}
        style={{
          display: 'grid',
          gridTemplateRows: isOpen ? '1fr' : '0fr',
          transition: 'grid-template-rows 220ms ease-out',
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          <div
            style={{
              padding: 'var(--evo-space-1) var(--evo-space-4) var(--evo-space-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--evo-space-4)',
            }}
          >
            {/* Iframe — only rendered while open (lazy mount) */}
            {isOpen && (
              <div
                style={{
                  border: '1px solid var(--evo-stroke)',
                  borderRadius: 'var(--evo-r-sm)',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                <iframe
                  src={previewUrl}
                  style={{
                    width: '100%',
                    height: '320px',
                    border: 'none',
                    display: 'block',
                  }}
                  title={t('projects.revisions.headerLabel', {
                    n: entry.revisionNumber,
                    date: approvedDate,
                  })}
                />
              </div>
            )}

            {/* Metadata — iterations + approval note (open-in-tab moved to header) */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--evo-space-4)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--evo-fs-label)',
                  color: 'var(--evo-fg-3)',
                  fontFamily: 'var(--evo-font)',
                }}
              >
                {t('projects.revisions.iterations', { count: entry.workingVersionsCount })}
              </span>

              {entry.approvalNote && (
                <span
                  style={{
                    fontSize: 'var(--evo-fs-label)',
                    color: 'var(--evo-fg-2)',
                    fontFamily: 'var(--evo-font)',
                  }}
                >
                  <span style={{ color: 'var(--evo-fg-3)', marginRight: 'var(--evo-space-1)' }}>
                    {t('projects.revisions.noteLabel')}
                  </span>
                  {entry.approvalNote}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── RevisionImagesPanel ───────────────────────────────────────────────────────
// Read-only list of pending images captured in the revision snapshot.

interface RevisionImagesPanelProps {
  projectId: number;
  revisionNumber: number;
}

function RevisionImagesPanel({ projectId, revisionNumber }: RevisionImagesPanelProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useRevisionPendingImages(projectId, revisionNumber);

  if (isLoading) {
    return (
      <div style={{ padding: 'var(--evo-space-4) 0', fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
        {t('projects.revisions.loadingImages', { defaultValue: 'Cargando…' })}
      </div>
    );
  }

  if (!data || data.images.length === 0) {
    return (
      <div style={{ padding: 'var(--evo-space-4) 0', fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
        {t('projects.revisions.noImages', { defaultValue: 'Sin imágenes pendientes' })}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 'var(--evo-space-4)' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 90px 1fr 90px',
        gap: 'var(--evo-space-3)',
        padding: 'var(--evo-space-2) var(--evo-space-3)',
        fontSize: 'var(--evo-fs-label)',
        color: 'var(--evo-fg-4)',
        fontFamily: 'var(--evo-font)',
        borderBottom: '1px solid var(--evo-stroke)',
      }}>
        <span>{t('projects.pending.colFile')}</span>
        <span>{t('projects.pending.colSize')}</span>
        <span>{t('projects.pending.colRole')}</span>
        <span>{t('projects.pending.colStatus')}</span>
      </div>
      {data.images.map(img => (
        <div key={img.filename} style={{
          display: 'grid',
          gridTemplateColumns: '1fr 90px 1fr 90px',
          gap: 'var(--evo-space-3)',
          padding: 'var(--evo-space-2) var(--evo-space-3)',
          alignItems: 'center',
          fontSize: 'var(--evo-fs-body)',
          fontFamily: 'var(--evo-font)',
          color: 'var(--evo-fg-2)',
        }}>
          <span style={{ color: 'var(--evo-fg-1)', fontFamily: 'monospace', fontSize: 'var(--evo-fs-label)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {img.filename}
          </span>
          <span style={{ color: 'var(--evo-fg-3)', fontSize: 'var(--evo-fs-label)' }}>
            {img.width}×{img.height}
          </span>
          <span style={{ color: 'var(--evo-fg-3)' }}>{img.role}</span>
          <span style={{ color: img.uploaded ? 'var(--evo-success, #22c55e)' : 'var(--evo-fg-4)', fontSize: 'var(--evo-fs-label)' }}>
            {img.uploaded
              ? t('projects.pending.statusUploaded')
              : t('projects.pending.statusPending')}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── RevisionsLedger ──────────────────────────────────────────────────────────
// Accordion with single-open-at-a-time behavior.
// Renders nothing if no revisions (per spec).
// Keyboard: Enter/Space toggle, Esc collapses open item.

export function RevisionsLedger({
  projectId,
  onNewRevision,
  isNewRevisionPending = false,
  newRevisionError,
}: RevisionsLedgerProps) {
  const { t } = useTranslation();
  const { data: revisions } = useProjectRevisions(projectId);
  const [openRevisionNumber, setOpenRevisionNumber] = useState<number | null>(null);
  const [imagesDrawerRevision, setImagesDrawerRevision] = useState<RevisionEntry | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Keyboard: Esc collapses the open item (or closes images drawer)
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // Images drawer takes priority
        if (imagesDrawerRevision !== null) return; // DrawerSurface handles its own Esc
        if (openRevisionNumber !== null) {
          if (containerRef.current?.contains(document.activeElement)) {
            setOpenRevisionNumber(null);
          }
        }
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openRevisionNumber, imagesDrawerRevision]);

  if (!revisions || revisions.length === 0) return null;

  // Display most recent first (DESC order)
  const sorted = [...revisions].sort(
    (a, b) => b.revisionNumber - a.revisionNumber,
  );

  function toggleRevision(n: number) {
    setOpenRevisionNumber(prev => (prev === n ? null : n));
  }

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        position: 'relative',
      }}
    >
      {/* Section title + optional "New revision" button */}
      <div
        style={{
          padding: 'var(--evo-space-3) var(--evo-space-4)',
          borderBottom: '1px solid var(--evo-stroke)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{
            fontSize: 'var(--evo-fs-label)',
            fontWeight: 'var(--evo-fw-semibold)',
            color: 'var(--evo-fg-2)',
            fontFamily: 'var(--evo-font)',
          }}
        >
          {t('projects.revisions.ledgerTitle')}
        </span>

        {onNewRevision && (
          <Button
            variant="outline"
            tone="muted"
            size="sm"
            disabled={isNewRevisionPending}
            loading={isNewRevisionPending}
            onClick={onNewRevision}
          >
            {t('projects.delivered.newRevisionButton')}
          </Button>
        )}
      </div>

      {/* Error from new revision mutation */}
      {newRevisionError && (
        <div
          role="alert"
          style={{
            padding: 'var(--evo-space-2) var(--evo-space-4)',
            fontSize: 'var(--evo-fs-label)',
            color: 'var(--evo-error, #e53e3e)',
            fontFamily: 'var(--evo-font)',
          }}
        >
          {newRevisionError}
        </div>
      )}

      {/* Accordion rows */}
      {sorted.map((entry: RevisionEntry) => (
        <RevisionItem
          key={entry.revisionNumber}
          entry={entry}
          projectId={projectId}
          isOpen={openRevisionNumber === entry.revisionNumber}
          onToggle={() => toggleRevision(entry.revisionNumber)}
          onOpenImages={() => setImagesDrawerRevision(entry)}
        />
      ))}

      {/* Images drawer — scoped to this container (position: relative above) */}
      {imagesDrawerRevision && (
        <DrawerSurface requestClose={() => setImagesDrawerRevision(null)}>
          <RevisionImagesPanel
            projectId={projectId}
            revisionNumber={imagesDrawerRevision.revisionNumber}
          />
        </DrawerSurface>
      )}
    </div>
  );
}
